import React from 'react';
import { CheckCircle, Clock, Shield, Users, Award, TrendingUp } from 'lucide-react';

const WhyChooseSection = ({ onOpenForm }) => {
  const reasons = [
    {
      icon: <CheckCircle size={32} />,
      title: 'Highest Success Rate',
      description: 'With a 95% approval rate, our expertise ensures your trademark application has the best chance of success.'
    },
    {
      icon: <Clock size={32} />,
      title: 'Fast Processing',
      description: 'We expedite your filing process and handle all USPTO communications promptly to avoid delays.'
    },
    {
      icon: <Shield size={32} />,
      title: 'Comprehensive Protection',
      description: 'We ensure your trademark is properly classified and protected across all relevant categories.'
    },
    {
      icon: <Users size={32} />,
      title: 'Dedicated Support',
      description: 'Your assigned case manager guides you through every step of the registration process.'
    },
    {
      icon: <Award size={32} />,
      title: 'Industry Expertise',
      description: '15+ years of specialized experience in trademark law and USPTO procedures.'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Transparent Pricing',
      description: 'No hidden fees or surprises. Clear pricing with flexible packages to fit your needs.'
    }
  ];

  return (
    <section id="why-choose" className="py-20 bg-gradient-to-br from-primary to-gray-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wide">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 text-white">
              The Clear Choice for Trademark Registration
            </h2>
            <p className="text-gray-300 max-w-3xl mx-auto text-lg">
              When it comes to protecting your brand, experience and expertise matter. Here's why thousands of businesses trust us with their trademark registrations.
            </p>
          </div>

          {/* Reasons Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {reasons.map((reason, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6 hover:bg-white/20 transition-all hover:border-secondary"
              >
                <div className="bg-secondary/20 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                  <div className="text-secondary">
                    {reason.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">{reason.title}</h3>
                <p className="text-gray-300 leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl font-bold text-secondary mb-2">1000+</div>
              <div className="text-sm text-gray-300">Trademarks Filed</div>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl font-bold text-secondary mb-2">95%</div>
              <div className="text-sm text-gray-300">Approval Rate</div>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl font-bold text-secondary mb-2">5000+</div>
              <div className="text-sm text-gray-300">Happy Clients</div>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl font-bold text-secondary mb-2">15+</div>
              <div className="text-sm text-gray-300">Years Experience</div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={onOpenForm}
              className="bg-secondary text-white px-8 py-4 rounded-md hover:bg-secondary/90 transition-all font-semibold shadow-lg text-lg"
            >
              Protect My Brand Today
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
